import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Upload, Button, Modal } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import apiService from '../api/apiService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DocumentModel = ({
  isOpen,
  onClose,
  onSave,
  existingData,
  guests = [],
  hotelId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const DOC_TYPE_OPTIONS = [
    { value: 'passport', label: 'Passport' },
    { value: 'adhaar_card', label: 'Aadhaar Card' },
    { value: 'pan_card', label: 'Pan Card' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    if (existingData) {
      form.setFieldsValue({
        guest: existingData.guest,
        docType: existingData.doc_type || '',
        description: existingData.description || '',
      });

      if (existingData.file_url) {
        setFileList([
          {
            uid: '-1',
            name: existingData.file_url.split('/').pop(),
            status: 'done',
            url: existingData.file_url,
          },
        ]);
      }
    }
  }, [existingData, form]);

  const handleFileChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const handleSaveAndEdit = async values => {
    setLoading(true);

    const formData = new FormData();
    formData.append('hotel', hotelId);
    formData.append('doc_type', values.docType);
    formData.append('description', values.description);
    formData.append('guest', values.guest);

    if (fileList.length > 0 && fileList[0].originFileObj) {
      formData.append('file', fileList[0].originFileObj);
    }

    try {
      let response;
      if (existingData) {
        response = await apiService.patch(
          `/documents/${existingData.id}/?hotel=${hotelId}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
        toast.success('Document updated successfully!');
      } else {
        response = await apiService.post(
          `/documents/?hotel=${hotelId}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
        toast.success('Document added successfully!');
      }

      if (response) {
        onSave(response);
        handleCancel(); // ✅ Reset the form only if save is successful
      }
    } catch (err) {
      console.error('Error saving document:', err);
      toast.error('Something went wrong while saving the document.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    form.resetFields();
    setFileList([]);
    setLoading(false);
  };

  return (
    <Modal
      centered
      title={existingData ? 'Edit Document' : 'Add Document'}
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
    >
      <Form form={form} layout="vertical" onFinish={handleSaveAndEdit}>
        <Form.Item
          label="Guest Name"
          name="guest"
          rules={[{ required: true, message: 'Please select a guest' }]}
        >
          <Select
            placeholder="Select guest"
            disabled={loading}
            options={guests.map(guest => ({
              label: guest.name,
              value: guest.id,
            }))}
          />
        </Form.Item>

        <Form.Item
          label="Doc Type"
          name="docType"
          rules={[{ required: true, message: 'Please select a document type' }]}
        >
          <Select placeholder="Select document type">
            {DOC_TYPE_OPTIONS.map(type => (
              <Select.Option key={type.value} value={type.value}>
                {type.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: 'Please enter the description' }]}
        >
          <Input.TextArea placeholder="Enter document description" />
        </Form.Item>

        <Form.Item
          label="Upload File"
          name="file"
          rules={[
            { required: !existingData, message: 'Please upload a document' },
          ]}
        >
          <Upload
            beforeUpload={() => false}
            fileList={fileList}
            onChange={handleFileChange}
            maxCount={1}
            listType="picture"
            onPreview={file =>
              window.open(file.url || URL.createObjectURL(file.originFileObj))
            }
          >
            <Button icon={<UploadOutlined />}>Click to Upload</Button>
          </Upload>
        </Form.Item>

        <div
          className="form-actions"
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <div />
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default DocumentModel;
